import React, { useEffect, lazy } from 'react';
import { Button } from '../components/ui/Button';
import { ArrowRight, ShieldCheck, Lock } from 'lucide-react';
import { useSpotCountdown } from '../hooks/useSpotCountdown';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { useDeadlineCountdown } from '../hooks/useDeadlineCountdown';
import { FloatingWhatsApp } from '../components/FloatingWhatsApp';
import { FloatingCta } from '../components/FloatingCta';
export function CheckoutPage() {
  const {
    spots
  } = useSpotCountdown();
  const {
    trackCustomEvent
  } = useMetaPixel();
  const countdown = useDeadlineCountdown();
  const ACTIVE_CHECKOUT_URL = 'https://www.fanbasis.com/agency-checkout/true-marketer/D9Vl5';
  useEffect(() => {
    trackCustomEvent('Checkout-Page-Vist');
  }, [trackCustomEvent]);
  const handleCheckout = () => {
    trackCustomEvent('Initiate-Checkout-97');
    window.location.href = ACTIVE_CHECKOUT_URL;
  };
  const pad = (n: number) => String(n).padStart(2, '0');
  return <div className="min-h-screen w-full flex flex-col items-center bg-[#FAFAFA] px-4 py-6 sm:py-10" data-id="element-236">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden" data-id="element-237">
        <div className="p-5 sm:p-8 space-y-5" data-id="element-238">
          {/* Header — compact, above the fold */}
          <div className="text-center space-y-1.5" data-id="element-239">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900" data-id="element-240">
              48 HRS AI CASH MACHINE
            </h1>
            <p className="text-gray-500 text-sm sm:text-base" data-id="element-241">
              Acceso Completo al Programa
            </p>
          </div>

          {/* Spots urgency pill */}
          {!countdown.isExpired && <div className={`text-center text-xs sm:text-sm font-bold uppercase tracking-wider py-2 px-3 rounded-lg transition-colors duration-300 ${spots === 1 ? 'text-red-700 bg-red-50 border border-red-200 animate-flash' : 'text-emerald-700 bg-emerald-50 border border-emerald-200'}`} data-id="element-242">
              🔥 Solo {spots}{' '}
              {spots === 1 ? 'Cupo Disponible' : 'Cupos Disponibles'}
            </div>}

          {/* Price block */}
          <div className="text-center py-1" data-id="element-243">
            <div className="text-[11px] sm:text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1" data-id="element-244">
              {countdown.isExpired ? 'Precio Regular' : 'Precio Especial Hoy'}
            </div>
            <div className="flex items-baseline justify-center gap-3" data-id="element-245">
              <span className="text-5xl sm:text-6xl font-bold text-gray-900 tracking-tight tabular-nums" data-id="element-246">
                {countdown.isExpired ? '$1997' : '$497'}
              </span>
              {!countdown.isExpired && <span className="text-lg sm:text-xl text-gray-400 line-through tabular-nums" data-id="element-247">
                  $1997
                </span>}
            </div>
          </div>

          {/* PRIMARY CTA — above the fold on mobile */}
          {!countdown.isExpired ? <div className="space-y-3" data-id="element-248">
              <Button onClick={handleCheckout} size="lg" className="w-full text-base sm:text-lg font-bold h-16 sm:h-[68px] shadow-lg shadow-emerald-600/20 ring-1 ring-emerald-500/30" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-250" />} data-id="element-249">
                Comprar Ahora
              </Button>
              <div className="flex items-center justify-center gap-4 text-[11px] sm:text-xs text-gray-500" data-id="element-251">
                <span className="flex items-center gap-1" data-id="element-252">
                  <Lock className="w-3 h-3" data-id="element-253" />
                  Pago seguro
                </span>
                <span className="flex items-center gap-1" data-id="element-254">
                  <ShieldCheck className="w-3 h-3" data-id="element-255" />
                  Garantía 30 días
                </span>
              </div>
            </div> : <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-2 text-center" data-id="element-256">
              <p className="text-base font-semibold text-gray-900" data-id="element-257">
                La oferta especial ha terminado
              </p>
              <p className="text-sm text-gray-600" data-id="element-258">
                Contáctanos por WhatsApp para conocer las opciones disponibles.
              </p>
            </div>}

          {/* Countdown — urgency reinforcer below CTA */}
          {!countdown.isExpired && <div className="bg-red-600 rounded-xl p-3.5 space-y-2" data-id="element-259">
              <p className="text-[10px] sm:text-xs uppercase tracking-wider text-red-100 font-semibold text-center" data-id="element-260">
                ⏳ El precio sube a $1997 el viernes en
              </p>
              <div className="flex justify-center items-baseline gap-1.5 sm:gap-3" data-id="element-261">
                {countdown.days > 0 && <>
                    <div className="flex flex-col items-center min-w-0 flex-1 max-w-[60px]" data-id="element-262">
                      <span className="text-xl sm:text-2xl font-bold text-white tabular-nums" data-id="element-263">
                        {pad(countdown.days)}
                      </span>
                      <span className="text-[9px] sm:text-[10px] uppercase text-red-200" data-id="element-264">
                        Días
                      </span>
                    </div>
                    <span className="text-xl sm:text-2xl font-bold text-red-300" data-id="element-265">
                      :
                    </span>
                  </>}
                <div className="flex flex-col items-center min-w-0 flex-1 max-w-[60px]" data-id="element-266">
                  <span className="text-xl sm:text-2xl font-bold text-white tabular-nums" data-id="element-267">
                    {pad(countdown.hours)}
                  </span>
                  <span className="text-[9px] sm:text-[10px] uppercase text-red-200" data-id="element-268">
                    Hrs
                  </span>
                </div>
                <span className="text-xl sm:text-2xl font-bold text-red-300" data-id="element-269">
                  :
                </span>
                <div className="flex flex-col items-center min-w-0 flex-1 max-w-[60px]" data-id="element-270">
                  <span className="text-xl sm:text-2xl font-bold text-white tabular-nums" data-id="element-271">
                    {pad(countdown.minutes)}
                  </span>
                  <span className="text-[9px] sm:text-[10px] uppercase text-red-200" data-id="element-272">
                    Min
                  </span>
                </div>
                <span className="text-xl sm:text-2xl font-bold text-red-300" data-id="element-273">
                  :
                </span>
                <div className="flex flex-col items-center min-w-0 flex-1 max-w-[60px]" data-id="element-274">
                  <span className="text-xl sm:text-2xl font-bold text-white tabular-nums" data-id="element-275">
                    {pad(countdown.seconds)}
                  </span>
                  <span className="text-[9px] sm:text-[10px] uppercase text-red-200" data-id="element-276">
                    Seg
                  </span>
                </div>
              </div>
            </div>}

          {/* Hero image — moved below CTA, lazy-loaded */}
          <img src="https://cdn.magicpatterns.com/uploads/itJEPFUgjYgyD9nVnBFzm6/Screenshot_2026-06-04_at_6.19.19_PM.png" alt="48 Hrs AI Cash Machine - Sistema completo" loading="lazy" decoding="async" className="w-full h-auto rounded-xl" data-id="element-277" />

          {/* Secondary CTA after image */}
          {!countdown.isExpired && <Button onClick={handleCheckout} size="lg" className="w-full text-base sm:text-lg font-semibold h-14 sm:h-16" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-279" />} data-id="element-278">
              Comprar Ahora
            </Button>}

          <p className="text-[11px] sm:text-xs text-gray-400 text-center pt-1" data-id="element-280">
            Serás redirigido a nuestra página de pago segura.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-6 mb-24 text-center text-gray-400 text-xs sm:text-sm px-4" data-id="element-281">
        &copy; {new Date().getFullYear()} AI Web Closer. Todos los derechos
        reservados.
      </div>

      {/* Sticky floating CTA — always-accessible buy button */}
      {!countdown.isExpired && <FloatingCta onClick={handleCheckout} price="$497" originalPrice="$1997" label="COMPRAR" showAfter={400} data-id="element-282" />}

      <FloatingWhatsApp link="https://wa.link/infocommerce" data-id="element-283" />
    </div>;
}